import * as React from "react";
import { Eye, EyeOff } from "lucide-react";

import { cn } from "@/lib/utils";

/**
 * A password field with a show/hide toggle (Login: Show Password Toggle, Oct 2026).
 *
 * - Starts hidden; the eye button on the right reveals it, the slashed eye hides it.
 * - Toggling keeps the caret/selection where it was and never touches the value.
 *   A mouse click on the eye doesn't steal focus from the field, so typing continues.
 * - Each instance has its own state, so confirm-password fields toggle separately.
 * - Hidden again when the surrounding form submits or resets (and on reload).
 * - The button is a real button: tab to it, Enter/Space toggles, and its
 *   accessible name reads "Show password" / "Hide password".
 * - The input stays `type="password"` until toggled, so browser autofill and
 *   password managers treat it as the password field it is. `id`, `name` and
 *   `autoComplete` pass straight through to the input.
 *
 * `className` styles the input itself (the wrapper only positions the button),
 * so it is a drop-in for `<Input type="password">` and plain `<input>`s alike.
 */
const PasswordInput = React.forwardRef<HTMLInputElement, Omit<React.ComponentProps<"input">, "type">>(
  ({ className, disabled, ...props }, ref) => {
    const [shown, setShown] = React.useState(false);
    const inputRef = React.useRef<HTMLInputElement | null>(null);
    // Selection captured just before a toggle, restored right after the type flips.
    const pendingSelection = React.useRef<{ start: number; end: number; refocus: boolean } | null>(null);
    const setRefs = React.useCallback(
      (node: HTMLInputElement | null) => {
        inputRef.current = node;
        if (typeof ref === "function") ref(node);
        else if (ref) ref.current = node;
      },
      [ref],
    );

    // Hide again when the form is submitted or reset.
    React.useEffect(() => {
      const form = inputRef.current?.form;
      if (!form) return;
      const hide = () => setShown(false);
      form.addEventListener("submit", hide);
      form.addEventListener("reset", hide);
      return () => {
        form.removeEventListener("submit", hide);
        form.removeEventListener("reset", hide);
      };
    }, []);

    // Switching the type can reset the caret in some browsers; put it back
    // before paint so the cursor never visibly jumps.
    React.useLayoutEffect(() => {
      const input = inputRef.current;
      const sel = pendingSelection.current;
      pendingSelection.current = null;
      if (!input || !sel) return;
      if (sel.refocus && document.activeElement !== input) input.focus();
      const restore = () => {
        try {
          input.setSelectionRange(sel.start, sel.end);
        } catch {
          /* some browsers refuse setSelectionRange on type="password"; the value is untouched either way */
        }
      };
      // Chrome rebuilds the field's inner editor on the next layout after a type
      // change and drops the selection; force that layout now, then restore.
      void input.offsetWidth;
      restore();
      // Belt and braces for engines that rebuild it later than that.
      requestAnimationFrame(() => {
        if (input.selectionStart !== sel.start || input.selectionEnd !== sel.end) restore();
      });
    }, [shown]);

    const toggle = () => {
      const input = inputRef.current;
      if (input && input.selectionStart != null && input.selectionEnd != null) {
        pendingSelection.current = {
          start: input.selectionStart,
          end: input.selectionEnd,
          refocus: document.activeElement === input,
        };
      }
      setShown((v) => !v);
    };

    const label = shown ? "Hide password" : "Show password";

    return (
      <div className="relative">
        <input
          {...props}
          ref={setRefs}
          type={shown ? "text" : "password"}
          disabled={disabled}
          className={cn(
            "flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-base ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            className,
            "pr-10",
          )}
        />
        <button
          type="button"
          onClick={toggle}
          // Keep focus (and the caret) in the field on mouse clicks; keyboard use is unaffected.
          onMouseDown={(e) => e.preventDefault()}
          disabled={disabled}
          aria-label={label}
          title={label}
          className="absolute inset-y-0 right-0 flex w-10 items-center justify-center rounded-r-md text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
        >
          {shown ? <EyeOff className="h-4 w-4" aria-hidden="true" /> : <Eye className="h-4 w-4" aria-hidden="true" />}
        </button>
      </div>
    );
  },
);
PasswordInput.displayName = "PasswordInput";

export { PasswordInput };
